import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { Button } from "reactstrap";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../CommonComponents/RightDrawer";
import {
  fieldsConfig,
  PaymentrequiredFields,
  PaymentFieldGroups,
} from "../../utils/fieldsConfig";
import { formatDate } from "../../utils/commonutils";
import { errorSummary, scrollToFirstError } from "../../utils/formErrors";
import MasterSelect from "../CommonComponents/MasterSelect";
import PasswordInput from "../CommonComponents/PasswordInput";
import CompanySelect from "../CommonComponents/CompanySelect";
import { FALLBACK_PAYMENT_STAGE, FALLBACK_PAYMENT_STATUS } from "../../utils/masterFallbacks";

const emptyForm = () =>
  Object.keys(fieldsConfig).reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, { customerId: null });

const gridSize = (columns) => (columns === 3 ? { xs: 12, sm: 4 } : { xs: 12, sm: 6 });

const companyFromPayment = (payment) => {
  if (payment?.customerId && typeof payment.customerId === "object") {
    return payment.customerId;
  }
  if (payment?.applicantName) {
    return {
      companyName: payment.applicantName,
      _id: payment.customerId || payment.applicantName,
    };
  }
  return null;
};

export const LitigationModal = ({
  isOpen,
  onClose,
  mode,
  paymentData,
  customerData,
  onSubmit,
}) => {
  const [formData, setFormData] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const next = emptyForm();
    Object.keys(fieldsConfig).forEach((key) => {
      const value = paymentData?.[key];
      next[key] = fieldsConfig[key].type === "date" ? formatDate(value) : value || "";
    });
    next.customerId = companyFromPayment(paymentData);
    if (next.customerId?.companyName) {
      next.applicantName = next.customerId.companyName;
    }
    setFormData(next);
    setErrors({});
    setSubmitError("");
  }, [paymentData, isOpen]);

  const validateForm = () => {
    const nextErrors = {};
    PaymentrequiredFields.forEach((field) => {
      if (field === "applicantName") {
        if (!formData.customerId && !formData.applicantName) {
          nextErrors[field] = "Company is required";
        }
        return;
      }
      if (!formData[field]) nextErrors[field] = `${fieldsConfig[field].label} is required`;
    });
    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm();
    const failed = PaymentrequiredFields.filter((field) => nextErrors[field]);
    if (failed.length) {
      setSubmitError("");
      scrollToFirstError(failed);
      return;
    }
    const company = formData.customerId;
    const result = await onSubmit({
      ...formData,
      applicantName: company?.companyName || formData.applicantName,
      customerId: company?._id || formData.customerId,
      applicantMobile: formData.applicantMobile || company?.mobile || "",
      applicantEmail: formData.applicantEmail || company?.email || "",
    });
    if (result?.ok === false) {
      const fieldErrors = result.fieldErrors || {};
      setErrors(fieldErrors);
      setSubmitError(result.message || "Please fix the highlighted fields.");
      scrollToFirstError(Object.keys(fieldErrors));
    }
  };

  const clearError = (name) =>
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _cleared, ...rest } = prev;
      return rest;
    });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    clearError(e.target.name);
  };

  const handleCompanyChange = (company) => {
    clearError("applicantName");
    setFormData((prev) => ({
      ...prev,
      customerId: company,
      applicantName: company?.companyName || "",
      applicantMobile: prev.applicantMobile || company?.mobile || "",
      applicantEmail: prev.applicantEmail || company?.email || "",
    }));
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose} kicker="Payment recovery">
        {mode === "add" ? "New recovery file" : "Edit recovery file"}
      </ModalHeader>
      <ModalBody>
        {submitError || Object.keys(errors).length ? (
          <p className="form-error-summary">{submitError || errorSummary(Object.keys(errors).length)}</p>
        ) : null}
        {PaymentFieldGroups.map((group) => (
          <div className="drawer-section" key={group.title}>
            <h3 className="drawer-section-title">{group.title}</h3>
            <Grid container spacing={1.5}>
              {group.fields.map((name) => {
                const field = fieldsConfig[name];
                const required = PaymentrequiredFields.includes(name);
                return (
                  <Grid item {...gridSize(group.columns)} key={name} data-field={name}>
                    <p className="small-text">
                      {field.label}
                      {required ? " *" : ""}
                    </p>
                    {field.type === "company" ? (
                      <div className={errors[name] ? "field-wrap-error" : undefined}>
                        <CompanySelect
                          options={customerData}
                          value={formData.customerId}
                          onChange={handleCompanyChange}
                          placeholder="Select company"
                        />
                      </div>
                    ) : field.master ? (
                      <div className={errors[name] ? "field-wrap-error" : undefined}>
                        <MasterSelect
                          variant="inquiry"
                          group={field.master}
                          fallback={
                            field.master === "payment_recovery_status"
                              ? FALLBACK_PAYMENT_STATUS
                              : FALLBACK_PAYMENT_STAGE
                          }
                          value={formData[name] || ""}
                          placeholder={`Select ${field.label.toLowerCase()}`}
                          onChange={(e) => {
                            setFormData({ ...formData, [name]: e.target.value });
                            clearError(name);
                          }}
                        />
                      </div>
                    ) : field.type === "password" ? (
                      <PasswordInput
                        className={errors[name] ? "input-main-error" : ""}
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        placeholder={field.label}
                        autoComplete="new-password"
                      />
                    ) : (
                      <input
                        className={`input-main${errors[name] ? " input-main-error" : ""}`}
                        type={field.type}
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        placeholder={field.type === "date" ? undefined : field.label}
                      />
                    )}
                    {errors[name] ? <p className="error-message">{errors[name]}</p> : null}
                  </Grid>
                );
              })}
            </Grid>
          </div>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button className="btn btn-border" onClick={onClose}>
          Cancel
        </Button>
        <Button className="btn btn-colored" onClick={handleSubmit}>
          {mode === "add" ? "Save file" : "Save changes"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
